import { Button } from '@/components/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Icon } from '@/components/ui/Icon';
import useAnalytics from '@/lib/analytics/useAnalytics';
import {
  type BubbleColorMenuItem,
  PdfColorSelector,
} from '@/lib/editor/tailwind/selectors/color-selector';
import { cn } from '@/utils/utils';
import type { AnnotationTooltipContentProps } from '@anaralabs/lector';
import { useSetAtom } from 'jotai';
import {
  Check,
  Copy,
  Ellipsis,
  MessageSquare,
  MessageSquareText,
  Trash,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { AnnotationNewComment } from './annotation-new-comment';
import { lastHighlightColorAtom } from './pdf-content';
import { useDocumentAnnotations } from './use-document-annotations';

export const Divider = () => <div className="bg-border w-px h-[18px]" />;

export const TooltipContent = ({
  annotation,
  onClose,
  setPosition,
}: AnnotationTooltipContentProps) => {
  const {
    handleUpdateAnnotation,
    handleDeleteAnnotation,
    handleCopyToChat,
    handleCopyContents,
  } = useDocumentAnnotations(annotation.metadata?.chatbotId as string);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const setLastHighlightColor = useSetAtom(lastHighlightColorAtom);
  const { track } = useAnalytics();

  const handleColorChange = (
    e: React.MouseEvent,
    colorChange: BubbleColorMenuItem,
  ) => {
    e.stopPropagation();
    handleUpdateAnnotation(annotation.id, {
      ...annotation,
      color: colorChange.color,
      borderColor: colorChange.borderColor,
    });
    track('annotation_color_updated', {
      location: 'pdf_viewer',
    });
    setLastHighlightColor(colorChange);
  };

  useEffect(() => {
    if (isAddingComment) {
      setPosition?.('bottom');
    } else {
      setPosition?.('top');
    }
  }, [isAddingComment]);

  const handleStartAddingComment = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAddingComment(true);
  }, []);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isConfirmingDelete) {
        setIsConfirmingDelete(true);
      } else {
        handleDeleteAnnotation(annotation.id);
        onClose?.();
        track('annotation_deleted', {
          location: 'pdf_viewer',
        });
      }
    },
    [annotation.id, handleDeleteAnnotation, onClose, isConfirmingDelete],
  );

  const handleTooltipClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConfirmingDelete(false);
  }, []);

  if (isAddingComment) {
    return <AnnotationNewComment annotation={annotation} onClose={onClose} />;
  }

  return (
    <div
      className="flex flex-row gap-0.5 items-center bg-popover border rounded-lg p-0.5 shadow-md"
      onClick={handleTooltipClick}
    >
      <PdfColorSelector
        currentColor={annotation.color || 'Orange'}
        onColorChange={handleColorChange}
      />
      <Divider />
      <Button
        variant="ghost"
        size="xs"
        className="h-[30px] font-medium hover:bg-popover-hover"
        onClick={handleStartAddingComment}
      >
        <Icon as={MessageSquareText} />
        Comment
      </Button>
      <Divider />
      <Button
        variant="ghost"
        size="xs"
        className={cn(
          'h-[30px] font-medium hover:bg-popover-hover',
          isConfirmingDelete &&
            'text-destructive-solid bg-destructive hover:!bg-destructive hover:!text-destructive-solid',
        )}
        onClick={handleDelete}
      >
        <Icon as={isConfirmingDelete ? Check : Trash} />
        {isConfirmingDelete ? 'Confirm' : 'Delete'}
      </Button>
      <Divider />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="text-foreground h-[30px] font-medium hover:bg-popover-hover"
            variant="ghost"
            size="xs"
          >
            <Icon as={Ellipsis} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[186px]" sideOffset={6}>
          <DropdownMenuItem
            onClick={() => {
              handleCopyContents({
                text: annotation.metadata?.text as string,
                comment: annotation.comment as string,
                chatbotId: annotation.metadata?.chatbotId as string,
              });
              track('annotation_comment_copied', {
                location: 'pdf_viewer',
              });
            }}
          >
            <Icon as={Copy} />
            Copy contents
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              handleCopyToChat(
                annotation.metadata?.text as string,
                annotation.highlights,
                annotation.metadata?.chatbotId as string,
              );
              track('annotation_added_to_chat', {
                location: 'pdf_viewer',
              });
            }}
          >
            <Icon as={MessageSquare} />
            Add to chat
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
